import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ForbiddenError, NotFoundError } from "~/api/errors";
import { useApi } from "~/api/provider";
import { queryKeys } from "~/api/queries";
import type { Document } from "~/api/types";

/** Resolve the actual path, including parents outside the sidebar's recent documents. */
export function useDocumentAncestors(document: Document | undefined) {
  const api = useApi();
  const client = useQueryClient();
  return useQuery({
    queryKey: ["documents", "ancestors", document?.id, document?.parentDocumentId],
    enabled: Boolean(document?.parentDocumentId),
    queryFn: async () => {
      const parents: Document[] = [];
      const seen = new Set([document?.id]);
      let id = document?.parentDocumentId;
      while (id && !seen.has(id)) {
        seen.add(id);
        const parentId = id;
        try {
          const parent = await client.fetchQuery({
            queryKey: queryKeys.document(parentId),
            queryFn: () => api.documents.get(parentId),
            staleTime: 30_000,
            retry: false,
          });
          parents.unshift(parent);
          id = parent.parentDocumentId;
        } catch (error) {
          if (error instanceof NotFoundError || error instanceof ForbiddenError) break;
          throw error;
        }
      }
      return parents;
    },
  });
}
